import { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowUpTrayIcon,
  FolderPlusIcon,
  FolderIcon,
  DocumentIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentCheckIcon,
  DocumentDuplicateIcon,
  ArrowsRightLeftIcon,
  TagIcon,
  ClockIcon,
  HomeIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { objectStorageService } from '../../services/objectStorageService';
import type { StorageBucket, StorageObject } from '../../types/objectStorage';
import PresignModal from './PresignModal';
import BulkDeleteBar from './BulkDeleteBar';
import ObjectMetadataPanel from './ObjectMetadataPanel';
import VersionHistory from './VersionHistory';
import { multipartUpload, type MultipartProgress } from '../../utils/multipartUpload';

export interface ObjectBrowserHandle {
  uploadFiles: (files: FileList) => void;
  cancelUpload: (fileId: string) => void;
  uploads: Map<string, MultipartProgress>;
}

interface Props {
  projectId: string;
  bucket: StorageBucket;
  embedded?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

const ObjectBrowser = forwardRef<ObjectBrowserHandle, Props>(function ObjectBrowser(
  { projectId, bucket, embedded },
  ref,
) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [prefix, setPrefix] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [presignKey, setPresignKey] = useState<string | null>(null);
  const [metadataKey, setMetadataKey] = useState<string | null>(null);
  const [versionsKey, setVersionsKey] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [copyMoveTarget, setCopyMoveTarget] = useState<{ key: string; action: 'copy' | 'move' } | null>(null);
  const [copyMoveDest, setCopyMoveDest] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [uploads, setUploads] = useState<Map<string, MultipartProgress>>(new Map());
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100 MiB

  const queryKey = ['objects', projectId, bucket.id, prefix];

  const { data: objects = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => objectStorageService.listObjects(projectId, bucket.id, prefix, false),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, key }: { file: File; key: string }) =>
      objectStorageService.uploadObject(projectId, bucket.id, file, key, setUploadProgress),
    onSuccess: () => {
      toast.success('File uploaded');
      setUploadProgress(null);
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['buckets', projectId] });
      queryClient.invalidateQueries({ queryKey: ['storage-usage', projectId] });
    },
    onError: (err: any) => {
      setUploadProgress(null);
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || 'Upload failed';
      if (status === 402) {
        toast((t) => (
          <div className="space-y-2">
            <p className="text-sm font-medium text-white">Plan limit reached</p>
            <p className="text-xs text-gray-300">{msg}</p>
            <div className="flex gap-2">
              <a
                href="/portal/wallet"
                onClick={() => toast.dismiss(t.id)}
                className="text-xs px-3 py-1 bg-primary-500 hover:bg-primary-600 text-white rounded"
              >
                Top up wallet
              </a>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-xs px-3 py-1 border border-gray-600 text-gray-300 rounded"
              >
                Dismiss
              </button>
            </div>
          </div>
        ), { duration: 15000, style: { background: '#1f2937', color: '#fff', maxWidth: 480 } });
        return;
      }
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => objectStorageService.deleteObject(projectId, bucket.id, key),
    onSuccess: () => {
      toast.success('Object deleted');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => toast.error('Delete failed'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (keys: string[]) => objectStorageService.bulkDeleteObjects(projectId, bucket.id, keys),
    onSuccess: (result) => {
      toast.success(`Deleted ${result.deleted.length} object${result.deleted.length !== 1 ? 's' : ''}`);
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => toast.error('Bulk delete failed'),
  });

  const copyMoveMutation = useMutation({
    mutationFn: ({ src, dst, action }: { src: string; dst: string; action: 'copy' | 'move' }) =>
      action === 'copy'
        ? objectStorageService.copyObject(projectId, bucket.id, src, dst)
        : objectStorageService.moveObject(projectId, bucket.id, src, dst),
    onSuccess: (_, vars) => {
      toast.success(`Object ${vars.action === 'copy' ? 'copied' : 'moved'}`);
      setCopyMoveTarget(null);
      setCopyMoveDest('');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => toast.error('Operation failed'),
  });

  const createFolderMutation = useMutation({
    mutationFn: (name: string) =>
      objectStorageService.createFolder(projectId, bucket.id, prefix + name),
    onSuccess: () => {
      toast.success('Folder created');
      setShowNewFolder(false);
      setNewFolderName('');
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => toast.error('Failed to create folder'),
  });

  const cancelUpload = (fileId: string) => {
    abortControllers.current.get(fileId)?.abort();
  };

  // Warn before navigating away with active uploads.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasActive = Array.from(uploads.values()).some(
        (u) => u.status === 'pending' || u.status === 'uploading',
      );
      if (!hasActive) return;
      e.preventDefault();
      e.returnValue = 'Uploads in progress - leaving will cancel them.';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [uploads]);

  // On unmount, abort every in-flight controller so multipartUpload's catch
  // handler fires and calls abortMultipart server-side.
  useEffect(() => {
    return () => {
      Array.from(abortControllers.current.values()).forEach((ctrl) => ctrl.abort());
      abortControllers.current.clear();
    };
  }, []);

  const uploadFiles = (files: FileList) => {
    Array.from(files).forEach((file) => {
      const key = prefix + file.name;
      if (file.size <= MULTIPART_THRESHOLD) {
        uploadMutation.mutate({ file, key });
        return;
      }
      // Multipart path for files > 100 MiB.
      const fileId = `${Date.now()}-${file.name}-${file.size}`;
      const ctrl = new AbortController();
      abortControllers.current.set(fileId, ctrl);
      setUploads((prev) => {
        const next = new Map(prev);
        next.set(fileId, {
          fileId,
          name: file.name,
          totalSize: file.size,
          uploadedBytes: 0,
          status: 'pending',
        });
        return next;
      });
      multipartUpload({
        projectId,
        bucketId: bucket.id,
        key,
        file,
        fileId,
        onProgress: (p) =>
          setUploads((prev) => {
            const next = new Map(prev);
            next.set(fileId, p);
            return next;
          }),
        signal: ctrl.signal,
      })
        .then(() => {
          // Refresh listing so the new object shows up.
          queryClient.invalidateQueries({ queryKey: ['objects', projectId, bucket.id, prefix] });
          queryClient.invalidateQueries({ queryKey: ['buckets', projectId] });
          queryClient.invalidateQueries({ queryKey: ['storage-usage', projectId] });
          // Clean up after a short delay so the user sees the "done" state.
          setTimeout(() => {
            setUploads((prev) => {
              const next = new Map(prev);
              next.delete(fileId);
              return next;
            });
            abortControllers.current.delete(fileId);
          }, 2000);
        })
        .catch(() => {
          // Error already recorded in state via onProgress.
          abortControllers.current.delete(fileId);
        });
    });
  };

  useImperativeHandle(
    ref,
    () => ({ uploadFiles, cancelUpload, uploads }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefix, uploadMutation.mutate, uploads],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) uploadFiles(e.target.files);
    e.target.value = '';
  };

  const handleDownload = async (key: string) => {
    // Open the tab synchronously (still inside the click's user-activation
    // window) and redirect it once the URL resolves - Safari (and increasingly
    // Chrome/Firefox) block window.open() called after an await.
    const tab = window.open('', '_blank');
    try {
      const { url } = await objectStorageService.presignObject(projectId, bucket.id, key, 'get', 15);
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      tab?.close();
      toast.error('Failed to generate download link');
    }
  };

  const navigateToFolder = (obj: StorageObject) => {
    if (obj.is_dir) setPrefix(obj.key);
  };

  const breadcrumbs = prefix.split('/').filter(Boolean);

  const toggleSelect = (key: string) => {
    const next = new Set(selected);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === objects.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(objects.map((o) => o.key)));
    }
  };

  // When embedded inside another positioned container (e.g. BucketModal's
  // FilesTab), drop our own `relative` so child modals/portals don't get a
  // stacking context they shouldn't inherit.
  return (
    <div className={embedded ? "space-y-3" : "space-y-3 relative"}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 text-sm min-w-0 flex-1">
          <button
            onClick={() => setPrefix('')}
            className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
          >
            <HomeIcon className="w-4 h-4" />
          </button>
          {breadcrumbs.map((crumb, i) => {
            const crumbPrefix = breadcrumbs.slice(0, i + 1).join('/') + '/';
            return (
              <span key={i} className="flex items-center gap-1 min-w-0">
                <ChevronRightIcon className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                <button
                  onClick={() => setPrefix(crumbPrefix)}
                  className="text-gray-300 hover:text-white truncate transition-colors"
                >
                  {crumb}
                </button>
              </span>
            );
          })}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowNewFolder(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            <FolderPlusIcon className="w-4 h-4" />
            New Folder
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Upload
          </button>
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} />
        </div>
      </div>

      {/* New folder input */}
      {showNewFolder && (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newFolderName) createFolderMutation.mutate(newFolderName);
              if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); }
            }}
            placeholder="Folder name"
            className="flex-1 bg-gray-900 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-primary-500"
          />
          <button
            onClick={() => newFolderName && createFolderMutation.mutate(newFolderName)}
            disabled={!newFolderName || createFolderMutation.isPending}
            className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            Create
          </button>
          <button
            onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}
            className="px-3 py-1.5 text-gray-400 hover:text-white text-sm transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Upload progress (single-PUT) */}
      {uploadProgress !== null && (
        <div className="bg-gray-700 rounded-full h-1.5">
          <div
            className="bg-primary-500 h-1.5 rounded-full transition-all"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      {/* Multipart upload progress - rendered as a portal so it floats above modals */}
      {uploads.size > 0 && createPortal(
        <div className="fixed bottom-4 right-4 z-[110] w-80 max-w-[calc(100vw-2rem)] space-y-2">
          {Array.from(uploads.values()).map((u) => (
            <UploadCard key={u.fileId} progress={u} onCancel={() => cancelUpload(u.fileId)} />
          ))}
        </div>,
        document.body,
      )}

      {/* Bulk delete bar */}
      {selected.size > 0 && (
        <BulkDeleteBar
          selectedCount={selected.size}
          onDelete={() => bulkDeleteMutation.mutate(Array.from(selected))}
          onCancel={() => setSelected(new Set())}
          isDeleting={bulkDeleteMutation.isPending}
        />
      )}

      {/* Copy/move destination input */}
      {copyMoveTarget && (
        <div className="flex items-center gap-2 bg-gray-900/60 rounded-lg px-3 py-2">
          <span className="text-sm text-gray-400 flex-shrink-0">
            {copyMoveTarget.action === 'copy' ? 'Copy' : 'Move'}{' '}
            <span className="font-mono text-gray-200">{copyMoveTarget.key}</span> to:
          </span>
          <input
            autoFocus
            value={copyMoveDest}
            onChange={(e) => setCopyMoveDest(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && copyMoveDest)
                copyMoveMutation.mutate({ src: copyMoveTarget.key, dst: copyMoveDest, action: copyMoveTarget.action });
              if (e.key === 'Escape') { setCopyMoveTarget(null); setCopyMoveDest(''); }
            }}
            placeholder="destination/path"
            className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-white font-mono focus:outline-none focus:border-primary-500"
          />
          <button
            onClick={() => copyMoveTarget && copyMoveDest && copyMoveMutation.mutate({ src: copyMoveTarget.key, dst: copyMoveDest, action: copyMoveTarget.action })}
            disabled={!copyMoveDest || copyMoveMutation.isPending}
            className="px-2 py-1 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm rounded transition-colors"
          >
            {copyMoveMutation.isPending ? '…' : 'Go'}
          </button>
          <button onClick={() => { setCopyMoveTarget(null); setCopyMoveDest(''); }} className="text-gray-400 hover:text-white text-sm">Cancel</button>
        </div>
      )}

      {/* Object table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="space-y-0">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 border-b border-gray-700/50 animate-pulse bg-gray-700/30" />
            ))}
          </div>
        ) : objects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <ArrowUpTrayIcon className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">Empty - upload files to get started</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-xs text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-3 text-left w-8">
                  <input
                    type="checkbox"
                    checked={selected.size === objects.length && objects.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-600 bg-gray-700 text-primary-500 focus:ring-0"
                  />
                </th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-right">Size</th>
                <th className="px-4 py-3 text-right">Modified</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {objects.map((obj) => (
                <tr
                  key={obj.key}
                  className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors group"
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(obj.key)}
                      onChange={() => toggleSelect(obj.key)}
                      className="rounded border-gray-600 bg-gray-700 text-primary-500 focus:ring-0"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => obj.is_dir ? navigateToFolder(obj) : undefined}
                      className={`flex items-center gap-2 text-left ${obj.is_dir ? 'cursor-pointer hover:text-primary-300' : 'cursor-default'} text-gray-200`}
                    >
                      {obj.is_dir ? (
                        <FolderIcon className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                      ) : (
                        <DocumentIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      )}
                      <span className="truncate max-w-xs">
                        {obj.key.replace(prefix, '')}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400 font-mono text-xs">
                    {obj.is_dir ? '-' : formatBytes(obj.size)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400 text-xs">
                    {new Date(obj.last_modified).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {!obj.is_dir && (
                        <>
                          <button
                            onClick={() => handleDownload(obj.key)}
                            title="Download"
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          >
                            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setPresignKey(obj.key)}
                            title="Generate URL"
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          >
                            <ClipboardDocumentCheckIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setCopyMoveTarget({ key: obj.key, action: 'copy' }); setCopyMoveDest(obj.key); }}
                            title="Copy"
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          >
                            <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setCopyMoveTarget({ key: obj.key, action: 'move' }); setCopyMoveDest(obj.key); }}
                            title="Move"
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          >
                            <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setMetadataKey(obj.key)}
                            title="Metadata"
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                          >
                            <TagIcon className="w-3.5 h-3.5" />
                          </button>
                          {bucket.versioning && (
                            <button
                              onClick={() => setVersionsKey(obj.key)}
                              title="Version history"
                              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                            >
                              <ClockIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => deleteMutation.mutate(obj.key)}
                        disabled={deleteMutation.isPending}
                        title="Delete"
                        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {presignKey && (
        <PresignModal
          projectId={projectId}
          bucketId={bucket.id}
          objectKey={presignKey}
          onClose={() => setPresignKey(null)}
        />
      )}
      {metadataKey && (
        <ObjectMetadataPanel
          projectId={projectId}
          bucketId={bucket.id}
          objectKey={metadataKey}
          onClose={() => setMetadataKey(null)}
        />
      )}
      {versionsKey && (
        <VersionHistory
          projectId={projectId}
          bucketId={bucket.id}
          objectKey={versionsKey}
          onClose={() => setVersionsKey(null)}
        />
      )}
    </div>
  );
});

export default ObjectBrowser;

function UploadCard({
  progress: u,
  onCancel,
}: {
  progress: import('../../utils/multipartUpload').MultipartProgress;
  onCancel: () => void;
}) {
  const pct = u.totalSize > 0 ? Math.min(100, Math.round((u.uploadedBytes / u.totalSize) * 100)) : 0;
  const isDone = u.status === 'done';
  const isError = u.status === 'error';
  const inFlight = !isDone && !isError;
  const sizeMB = (n: number) => (n / (1024 * 1024)).toFixed(1);

  return (
    <div className={`rounded-lg border p-3 shadow-lg backdrop-blur ${
      isError
        ? 'border-red-500/40 bg-red-950/80'
        : isDone
        ? 'border-emerald-500/40 bg-gray-900/90'
        : 'border-gray-700 bg-gray-900/90'
    }`}>
      <div className="flex items-center gap-2 mb-1.5">
        <p className="flex-1 text-xs font-medium text-white truncate" title={u.name}>{u.name}</p>
        {inFlight && (
          <button
            onClick={onCancel}
            className="p-0.5 rounded hover:bg-gray-800 text-gray-400 hover:text-red-300"
            aria-label="Cancel upload"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-gray-300">
        <span className="font-mono">
          {sizeMB(u.uploadedBytes)} / {sizeMB(u.totalSize)} MB
        </span>
        <span className={`ml-auto font-medium ${
          isError ? 'text-red-300' : isDone ? 'text-emerald-300' : 'text-primary-300'
        }`}>
          {isError ? `failed: ${u.error}` : isDone ? '✓ Done' : `${pct}%`}
        </span>
      </div>
      <div className="mt-1.5 h-1 rounded-full bg-gray-800 overflow-hidden">
        <div
          className={`h-full transition-all ${
            isError ? 'bg-red-500' : isDone ? 'bg-emerald-500' : 'bg-primary-500'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
